import React, { HTMLAttributes, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { useConfiguration } from '../../contexts/ConfigurationContext';
import * as QPlayerSVGElement from '../QPlayerSVGElement';
import { PollMessage, PollPanelHeader, PollPanelHeaderTitle, PollPanelWrapper, PollQuestion, PollQuestionTitle } from '../Poll/Poll.styled';

interface QuestionPanelProps extends React.DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	t: any;
	settings?: any;
	qna?: any;
	noShadow?: boolean;
	isMobile?: boolean;
	onSendQuestionClick?: (_event: React.MouseEvent | React.KeyboardEvent, _question: any) => void;
	onFocusOnQuestionField?: () => void;
}

const QuestionPanelInput = styled.textarea`
	qplayer-embed .qplayer-app & {
		text-align: left;
		font-weight: normal;
		font-size: 0.75em;
		font-family: inherit;
		width: 100%;
		max-width: 100%;
		min-width: 100%;
		max-height: 150px;
		min-height: 50px;
		box-sizing: border-box;
		padding: 10px;
		border: 1px solid #c5ced3;
		border-radius: 4px;
		overflow: auto;
		background-color: #ffffff;

		:empty:before {
			content: attr(placeholder);
			cursor: text;
		}
	}
`;

const QuestionPanelCharacterCount = styled.div`
	qplayer-embed .qplayer-app & {
		letter-spacing: 0px;
		color: #000000;
		opacity: 0.6;
		font-size: 0.75em;
		text-align: right;
		margin-bottom: 4px;
	}
`;

const QuestionPanelAction = styled.div`
	qplayer-embed .qplayer-app & {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		margin-top: 6px;
	}
`;

const QuestionPanelActionButton = styled.button<{ primaryColor?: string }>`
	qplayer-embed .qplayer-app & {
		height: 32px;
		border: none;
		background-color: ${(props) => props.primaryColor};
		border-radius: 4px;
		box-shadow: 0px 3px 3px #0000005c;
		color: #ffffff;
		font-family: inherit;
		font-weight: normal;
		font-size: 0.75em;
		padding: 0px 10px;

		&:disabled {
			opacity: 0.4;
			box-shadow: none;
		}
	}
`;

const QUESTION_TEXT_MAX_LENGTH = 300;

const QuestionPanel: React.FC<QuestionPanelProps> = ({ t, qna, settings, noShadow, isMobile, onSendQuestionClick, onFocusOnQuestionField }) => {
	const { isChatActivated, setPollExpanded } = useConfiguration();

	const [isExpanded, setExpanded] = useState<boolean>(true);
	const [isQuestionSent, setIsQuestionSent] = useState(false);
	const [questionText, setQuestionText] = useState('');

	const primaryColor = settings?.playerStyling?.primaryColor ?? '#126cfc';

	const onSendButtonClick = useCallback(
		(e) => {
			onSendQuestionClick?.(e, {
				text: questionText,
			});

			setQuestionText('');
			setIsQuestionSent(true);
			setExpanded(false);
		},
		[onSendQuestionClick, questionText]
	);

	const onQuestionTextChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
		setQuestionText(event.target.value.slice(0, QUESTION_TEXT_MAX_LENGTH));
	}, []);

	useEffect(() => {
		if (!isQuestionSent) {
			return;
		}
		setTimeout(() => setIsQuestionSent(false), 3000);
	}, [isQuestionSent]);

	useEffect(() => {
		setPollExpanded(isExpanded);
	}, [isExpanded]);

	return qna ? (
		<PollPanelWrapper className="qna__wrapper" isChatActivated={isChatActivated} noShadow={noShadow} isMobile={isMobile}>
			<PollPanelHeader onClick={() => setExpanded((prev) => !prev)} primaryColor={primaryColor === '#126cfc' ? undefined : primaryColor}>
				<PollPanelHeaderTitle>
					<QPlayerSVGElement.Question color={primaryColor === '#126cfc' ? undefined : primaryColor} />
					Q&A
				</PollPanelHeaderTitle>
				{isExpanded ? <QPlayerSVGElement.DownIcon /> : <QPlayerSVGElement.UpIcon />}
			</PollPanelHeader>

			{isQuestionSent && <PollMessage>{t('QUESTION_MESSAGE')}</PollMessage>}

			{!isQuestionSent && (
				<PollQuestion onClick={() => setExpanded((prev) => !prev)} style={{ cursor: 'pointer' }}>
					<PollQuestionTitle isExpanded={isExpanded}>{qna.text}</PollQuestionTitle>
				</PollQuestion>
			)}

			{!isQuestionSent && isExpanded && (
				<>
					<QuestionPanelCharacterCount>
						{questionText.length}/{QUESTION_TEXT_MAX_LENGTH} {t('LABEL_CHARACTERS')}
					</QuestionPanelCharacterCount>
					<QuestionPanelInput
						placeholder={t('QUESTION_PLACEHOLDER')}
						value={questionText}
						maxLength={QUESTION_TEXT_MAX_LENGTH}
						onChange={onQuestionTextChange}
						onFocus={onFocusOnQuestionField}
					/>

					<QuestionPanelAction>
						<QuestionPanelActionButton
							disabled={!questionText}
							onClick={onSendButtonClick}
							primaryColor={primaryColor === '#126cfc' ? undefined : primaryColor}
						>
							{t('LABEL_QUESTION_ACTION')}
						</QuestionPanelActionButton>
					</QuestionPanelAction>
				</>
			)}
		</PollPanelWrapper>
	) : null;
};

export default QuestionPanel;
